import {
  HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest,
} from '@angular/common/http';
import { inject, Inject, Injectable, InjectionToken } from '@angular/core';

import { OAuthService } from 'angular-oauth2-oidc';
//import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ToastService } from 'src/app/core/services/toast.service';
import { LoadSpinnerService } from 'src/app/shared/loaders/spinner/loadspinner.service';
import { environment } from 'src/environments/environment';

import { CachingStrategyService } from '../caching';
import { ErrorhanderService } from '../services/error-handler.service';

export const TRACKREQ_ENABLED = new InjectionToken<boolean>('overridectx');
export const OVERRIDE_CTX = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private _errorService = inject(ErrorhanderService);
  private _loaderService = inject(LoadSpinnerService);
  private _oauthService = inject(OAuthService);

  // Track if we're currently refreshing the token
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private strategy: CachingStrategyService, private _toastrService: ToastService,
    @Inject(TRACKREQ_ENABLED) protected track_Request: boolean) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ignoreHndl = req.context.get(OVERRIDE_CTX);
    if (this.strategy.isCacheable(req)) {
      console.info(`Tracking[${ignoreHndl && this.track_Request}]`, req.url);
    }

    return next.handle(req).pipe(
      catchError((error): Observable<HttpEvent<any>> => {
        if (
          (ignoreHndl && req.url?.endsWith('doLoanCreate')) ||
          req.url?.endsWith('submitClientDetails') ||
          req.url?.endsWith('doLoanQuote')
        ) {
          return throwError(() => error);
        }

        return this.handleError(error, req, next, ignoreHndl) as Observable<HttpEvent<any>>;
      }),
    );
  }

  private handleError(
    returnedError: HttpErrorResponse,
    request: HttpRequest<any>,
    next: HttpHandler,
    ignoreHndl: boolean
  ): any {
    const errorService = this._errorService;
    const loaderService = this._loaderService;
    const handled: boolean = false;

    if (!handled && returnedError instanceof Error && !request.url?.endsWith('doLoanCreate')) {
      console.error('ABNORMAL Error: ', returnedError.message);
      errorService.handle(returnedError, `ABNORMAL Error`, true);
      // of(error) this basically handles the error and resumes normal execution
      return throwError(() => returnedError);
    }

    // **  TIMEOUT ERROR - handled by timeout interceptor ** //
    if (!handled && returnedError instanceof TimeoutError) {
      return throwError(() => returnedError);
    }

    if (!handled && returnedError instanceof HttpErrorResponse) {
      if(returnedError.status === 401) {
        console.warn('401 Unauthorized - Attempting token refresh');

        // Attempt to refresh the token and retry the request
        return this.handle401Error(request, next);
      }


      let errorMessage = '';
      // **  ERROR EVENT ** //
      if (returnedError.error) {
        errorMessage = `Error: ${returnedError.error.message || returnedError.message}`;
        console.error(errorMessage);
        const newerror = new Error(errorMessage);
        errorService.handle(newerror, `Response Error`, true);
        loaderService.stopLoading();
        return of(returnedError);
        //of(returnedError);
      }

      // **  HTTP RESPONSE ERROR ** //
      // **  PROGRESS ERROR ** //
      if (returnedError.status == 0 && returnedError.error instanceof ProgressEvent) {
        if (!returnedError.url?.endsWith('doLoanCreate')) {
          const message1 = returnedError.url ?? '';
          const hdrString1 = `Network error`;
          const showdateStr1 = new Date().toLocaleString();

          this._toastrService.error(`${showdateStr1} : ${message1}`, hdrString1, {
            positionClass: 'toast-top-right',
            closeButton: true
          });
          loaderService.stopLoading();
          return of(returnedError);
        }
      }

      const newError = new Error();
      switch (returnedError.status) {
        case 302: // Found REsponse
          newError.message = 'Duplicate Record Found!';
          errorService.handle(newError, `LMS Server Duplicate not allowed!!`, true);
          return throwError(() => newError);
          break;
        case 417:
          // NUPAY Error response
          console.error('LMS SERVER ::', returnedError.error.message);
          newError.message = returnedError?.error!.message || returnedError.message;

          errorService.handle(newError, `LMS Server [response]`, true);
          return of(returnedError);
          break;
        case 501:
          errorService.handleOffline(returnedError?.error!.message || 'Server Not Responding', 'Server OFFLINE');
          return of(returnedError);
          break;
        case 400: // Bad Request

          this._toastrService.error(
            `${new Date().toLocaleString()} Server response:<br><br>${returnedError.error!.message ?? ''}`,
            'Bad Request error',
            {
              enableHtml: true,
              positionClass: 'toast-top-right',
              timeOut: 18000,
              tapToDismiss: true,
            }
          );
          loaderService.stopLoading();
          return of(returnedError);
          //handled = handleBadRequest(returnedError);
          break;
        case 504: // Bad Gateway
          const message1 = returnedError.message || returnedError.error?.message || '';

          this._toastrService.error(
            `${new Date().toLocaleString()} Server response:<br><br>${message1 || ''}`,
            'Bad Request error',
            {
              enableHtml: true,
              positionClass: 'toast-top-right',
              timeOut: 18000,
              tapToDismiss: true,
            }
          );
          loaderService.stopLoading();
          return of(returnedError);
          ///handled = handleBadGateway(returnedError);
          break;
        default:
          if (returnedError.error && returnedError.error == 'Unauthorized' && returnedError.status == 401) {
            errorService.handleString('Unauthorized recieved. Require login');
          } else if (
            returnedError.error &&
            returnedError.error.type == 'error' &&
            returnedError.statusText === 'Unknown Error'
          ) {
            //Offline
            errorService.handleOffline(returnedError.message || returnedError.error.message, 'Server OFFLINE');
          } else {
            if (returnedError.error && !returnedError.error!.result) {
              errorService.handleServerSideError(returnedError);
              break;
            }

            if (
              (returnedError.error?.trace || returnedError.error?.stackTrace) &&
              !environment?.production &&
              !!environment?.showtrace
            ) {
              errorService.handleStack(returnedError.error?.trace || returnedError.error?.stackTrace);
            }
          }
          if (
            (returnedError.error?.trace || returnedError.error?.stackTrace) &&
            !environment?.production &&
            !!environment?.showtrace
          ) {
            errorService.handleStack(returnedError.error?.trace || returnedError.error?.stackTrace);
          }
          break;
      }
      if (handled) {
        return of(returnedError);
      }
    }

    if (!handled) {
      return throwError(() => returnedError);
    }

    return of(returnedError);
  }

  /**
   * Handle 401 errors by attempting to refresh the access token
   * If refresh succeeds, retry the original request with the new token
   * If refresh fails, force logout and redirect to login
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      console.log('🔄 Attempting to refresh access token...');

      // Attempt token refresh using OAuthService
      return new Observable((observer) => {
        this._oauthService.refreshToken()
          .then(() => {
            console.log('✅ Token refresh successful');
            this.isRefreshing = false;

            // Get the new access token
            const newToken = this._oauthService.getAccessToken();
            this.refreshTokenSubject.next(newToken);

            // Clone the request with the new token
            const clonedRequest = this.addToken(request, newToken);

            // Retry the original request with the new token
            next.handle(clonedRequest).subscribe({
              next: (event) => observer.next(event),
              error: (err) => observer.error(err),
              complete: () => observer.complete()
            });
          })
          .catch((error) => {
            console.error('❌ Token refresh failed:', error);
            this.isRefreshing = false;
            this.refreshTokenSubject.next(null);

            // Refresh failed - token is truly invalid
            this._toastrService.error(
              'Your session has expired. Redirecting to login...',
              'Session Expired',
              {
                positionClass: 'toast-top-center',
                timeOut: 3000,
                closeButton: false
              }
            );

            this._loaderService.stopLoading();

            // Clear invalid local tokens/state and navigate to root/login
            console.warn('⚠️ Session expired - clearing tokens and redirecting to login');
            this._oauthService.logOut(true);

            // Navigate to root route after short delay to show toast
            setTimeout(() => {
              window.location.href = window.location.origin;
            }, 2000);

            observer.error(error);
          });
      });
    } else {
      // Token refresh is already in progress
      // Wait for the refresh to complete, then retry with the new token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const clonedRequest = this.addToken(request, token);
          return next.handle(clonedRequest);
        })
      );
    }
  }

  /**
   * Add the access token to the request headers
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
